
import React from 'react';
import { 
  Search, Megaphone, Bot, Palette, Code, Headset, Cpu, ShoppingBag, 
  GraduationCap, BadgeDollarSign, Scale, ShieldCheck, FileText, 
  Briefcase, UserCircle, LayoutGrid
} from 'lucide-react';
import { CATEGORIES } from '../../constants/serviceConstants';

const iconMap = {
  Megaphone, Bot, Palette, Code, Headset, Cpu, ShoppingBag,
  GraduationCap, BadgeDollarSign, Scale, ShieldCheck, FileText,
  Briefcase, UserCircle
};

const categoryColors = [
  'bg-orange-500', // Marketing
  'bg-violet-500', // AI
  'bg-pink-500',   // Design
  'bg-blue-500',   // Dev
  'bg-emerald-500', // Virtual
  'bg-cyan-500',   // Automation
  'bg-rose-500',   // Ecommerce
  'bg-amber-500',  // Education
  'bg-teal-500',   // Finance
  'bg-indigo-500', // Legal
];

export const Hero = ({ onSearch, onCategorySelect, className = '' }) => {
  const [inputValue, setInputValue] = React.useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(inputValue);
  };

  // We'll show the top 8 categories for quick access
  const topCategories = CATEGORIES.slice(0, 8);

  return (
    <div className={`relative pt-8 pb-16 md:pt-12 md:pb-16 overflow-hidden ${className}`}>
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#190192]/40 via-purple-200 to-rose-200 opacity-80" />
      
      {/* Bottom Fade to White */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
          What service do you <br className="hidden md:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
            need today?
          </span>
        </h1>

        {/* Search Bar */}
        <div className="max-w-xl mx-auto mb-10">
          <form onSubmit={handleSubmit} className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-indigo-400 group-focus-within:text-indigo-600 transition-colors" />
            </div>
            <input
              type="text"
              className="block w-full pl-12 pr-4 py-3.5 rounded-full border-0 shadow-lg ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 focus:shadow-xl transition-all duration-300 text-lg bg-white/90 backdrop-blur-sm focus:bg-white"
              placeholder="Search services..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button 
              type="submit"
              className="absolute right-2 top-2 bottom-2 bg-indigo-600 text-white px-5 rounded-full font-medium hover:bg-indigo-700 transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        {/* Quick Categories */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-6">
          {topCategories.map((cat, idx) => {
            const Icon = iconMap[cat.iconName] || LayoutGrid;
            const colorClass = categoryColors[idx % categoryColors.length];
            
            return (
              <button
                key={cat.id}
                onClick={() => onCategorySelect(cat.id)}
                className="flex flex-col items-center gap-1.5 group"
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center text-white shadow-md transform group-hover:scale-110 transition-transform duration-300 ${colorClass}`}>
                  <Icon className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <span className="text-xs font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
                  {cat.name.split(' ')[0]}
                </span>
              </button>
            );
          })}
          
          <button
            onClick={() => onCategorySelect('all')}
            className="flex flex-col items-center gap-1.5 group"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center bg-white text-slate-500 shadow-md border border-slate-200 transform group-hover:scale-110 transition-transform duration-300">
              <LayoutGrid className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <span className="text-xs font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
              View All
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};